import { IonAvatar, IonSpinner } from '@ionic/react';
import { useMutation } from '@apollo/client/react';
import {
  UPLOAD_PROFILE_PICTURE,
  DELETE_PROFILE_PICTURE,
} from '../../../../graphql/mutations/profilePicture';
import { EditableImage } from '../../../components/photo-picker/EditableImage';
import type { BasicUserAccount } from '../../../../contexts/UserAccountContext';
import type {
  UploadProfilePictureMutation,
  UploadProfilePictureMutationVariables,
  DeleteProfilePictureMutation,
} from '../../../../types/graphql-generated';
import { ProfileAvatarImage } from './ProfileAvatarImage';

interface Props {
  userAccount: BasicUserAccount;
  refetch: () => void;
  /** `lg` is the large standalone avatar; `sm` is the compact profile-header size. */
  size?: 'lg' | 'sm';
}

export const ProfileAvatar: React.FC<Props> = ({ userAccount, refetch, size = 'lg' }) => {
  const isSm = size === 'sm';
  const avatarClass = isSm
    ? 'w-[76px] h-[76px] border-2 border-white shadow-md'
    : 'w-48 h-48 border-4 border-white shadow-lg';
  const editButtonClass = isSm
    ? 'w-8 h-8'
    : 'bottom-2 right-2 w-10 h-10 shadow-lg';
  const editIconClass = isSm ? 'text-base' : 'text-xl';

  const [uploadProfilePicture, { loading: uploading }] = useMutation<
    UploadProfilePictureMutation,
    UploadProfilePictureMutationVariables
  >(UPLOAD_PROFILE_PICTURE);
  const [deleteProfilePicture, { loading: deleting }] = useMutation<DeleteProfilePictureMutation>(
    DELETE_PROFILE_PICTURE
  );

  // Refetches the account (and its updatedAt cache-buster) only after a successful change.
  const refetchOnSuccess = (errors: readonly string[] | undefined) => {
    if (!errors || errors.length === 0) refetch();
    return errors;
  };

  return (
    <div className="inline-block" data-testid="profile-avatar-container">
      <EditableImage
        loading={uploading || deleting}
        hasImage={!!userAccount.picture}
        onUpload={async (photo) => {
          const result = await uploadProfilePicture({
            variables: { imageBase64: photo.base64, contentType: photo.contentType },
          });
          return refetchOnSuccess(result.data?.uploadProfilePicture?.errors);
        }}
        onDelete={async () => {
          const result = await deleteProfilePicture();
          return refetchOnSuccess(result.data?.deleteProfilePicture?.errors);
        }}
        modalTitle="Foto de perfil"
        editLabel="Cambiar foto de perfil"
        uploadErrorMessage="No se pudo subir la foto. Inténtalo de nuevo."
        deleteErrorMessage="No se pudo eliminar la foto. Inténtalo de nuevo."
        buttonClassName={editButtonClass}
        iconClassName={editIconClass}
        buttonTestId="edit-photo-button"
        loadingContent={
          <IonAvatar className={avatarClass}>
            <div className="w-full h-full flex items-center justify-center bg-gray-100 rounded-full">
              <IonSpinner name="crescent" />
            </div>
          </IonAvatar>
        }
      >
        <ProfileAvatarImage
          picture={userAccount.picture}
          updatedAt={userAccount.updatedAt}
          alt={`Foto de perfil de ${userAccount.fullName || userAccount.email}`}
          className={avatarClass}
        />
      </EditableImage>
    </div>
  );
};
